import { formatDeviceName } from '../util/deviceName';
import endpoint from '../util/endpoint';
import { endSession, updateSessionCustomerLabels, updateSessionLocations, updateSessionWeights } from './sessionMonitorDetails.store';
import cartService from '@/carts/cart.service';
import { CartMap } from '@/carts/gps/cartGps';
import { awaitStoreGuid } from '@/lib/awaitStoreGuid';
import { storeManagerEndpoints } from '@/lib/endpoints';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import { SessionActiveDevice } from '@whiz-cart/node-shared/models/session/activeDevice';
import { SessionMonitorSessionDeviceMetadata } from '@whiz-cart/node-shared/models/session/sessionMonitor';
import { CustomerTicket, StoreCustomerTicket } from '@whiz-cart/node-shared/models/ticket/ticket';
import { createStore } from 'cross-state';
import _ from 'lodash';

export interface SessionMonitorState {
    hasLoadedOnce: boolean;
    isLoading: boolean;
    devices: SessionActiveDevice[];
    cartsLocations?: CartMap[];
}

export const sessionMonitorStore = createStore<SessionMonitorState>({ hasLoadedOnce: false, isLoading: false, devices: [] });

const transformDevice = ({
    deviceId,
    targetId,
    deviceMetadata,
    ...device
}:
    | (SessionActiveDevice & { deviceId?: string | null })
    | (CustomerTicket & { targetId?: string; deviceMetadata?: SessionMonitorSessionDeviceMetadata; isMobile?: boolean })) =>
    _.omit(
        {
            ...device,
            deviceId: formatDeviceName(deviceId ?? targetId ?? '', device.isMobile ?? false),
            deviceMetadata: deviceMetadata ?? (device.isMobile ? { hasScale: 'false' } : undefined),
        },
        ['isSessionOpen', 'hasTicket'],
    );

const isTicketActive = (t?: CustomerTicket) => t && !t.closedOn;

const updateSession = (
    devices: SessionActiveDevice[],
    find: string | ((d: SessionActiveDevice) => unknown),
    update: (d?: SessionActiveDevice) => SessionActiveDevice,
    upsert?: boolean,
) => {
    const _find = find instanceof Function ? find : (d: SessionActiveDevice) => d.deviceId === find;
    let found;

    devices = devices.map((d) => {
        if (!_find(d)) return d;
        found = true;
        return update(d);
    });

    if (!found && upsert) devices.push(update());
    return devices;
};

const updateTicketOfDevices = (devices: SessionActiveDevice[], id: string, update?: StoreCustomerTicket, upsert?: boolean) => {
    return updateSession(
        devices,
        (d) => d.tickets?.find((t) => t.id === id) || d.deviceId === update?.deviceId,
        (d) => {
            let found = false;
            let updatedTicket: StoreCustomerTicket | undefined = undefined;
            const tickets = (d?.tickets || []).map((t) => {
                if (t.id !== id) return t;
                found = true;
                return (updatedTicket = update);
            });

            if (!found && upsert) tickets.push((updatedTicket = update));
            return { deviceId: updatedTicket?.deviceId, ...d, tickets: tickets.filter(isTicketActive) as StoreCustomerTicket[] };
        },
        upsert,
    );
};

const setDevices = (update: (devices: SessionActiveDevice[]) => SessionActiveDevice[]) => sessionMonitorStore.set('devices', update);

export const setDevicesLoading = (loading: boolean) => sessionMonitorStore.set('isLoading', loading);

export const fetchSessions = async (storeGuid: string) => {
    const devices = await storeManagerEndpoints.session.activeDevices({ storeGuid }).fetch();

    sessionMonitorStore.set((state) => ({
        ...state,
        hasLoadedOnce: true,
        isLoading: false,
        devices: devices.map(transformDevice) as SessionActiveDevice[],
    }));

    return devices;
};

export const fetchCartsLocations = async (storeGuid: string) => {
    const cartsLocations = await cartService.getLocations({ storeGuid }).get();
    const parsedLocations = cartsLocations?.map((location) => {
        return {
            ...location,
            lat: location?.locations?.[0]?.lat ?? 0,
            lon: location?.locations?.[0]?.lon ?? 0,
            lng: location?.locations?.[0]?.lon ?? 0,
            createdOn: location?.locations?.[0]?.createdOn,
            deviceId: location?.cartId ?? '',
        };
    }) as CartMap[];

    sessionMonitorStore.set('cartsLocations', parsedLocations);

    return parsedLocations;
};

export const updateDevice = (device: SessionActiveDevice) => {
    const payload = transformDevice(device) as SessionActiveDevice;

    setDevices((devices) =>
        updateSession(
            devices.filter((d) => d.deviceId === payload.deviceId || d.sessionGuid !== payload.sessionGuid),
            payload.deviceId ?? '',
            (d) => ({ ...d, ...payload }),
            true,
        ),
    );
};

export const updateCartLocation = (payload: {
    storeGuid: string;
    deviceId: string;
    location: { lat: number; lon: number; createdOn: Date };
}) =>
    sessionMonitorStore.set('cartsLocations', (cartsLocations) =>
        cartsLocations?.find((c) => c?.cartId === payload?.deviceId)
            ? cartsLocations.map((cartLocation) => {
                  if (cartLocation.cartId !== payload.deviceId) {
                      return cartLocation;
                  }

                  return {
                      ...cartLocation,
                      lat: payload.location.lat,
                      lon: payload.location.lon,
                      lng: payload.location.lon,
                      createdOn: payload.location.createdOn,
                  };
              })
            : ([
                  ...(cartsLocations ?? []),
                  {
                      storeGuid: payload.storeGuid,
                      cartGuid: payload.deviceId,
                      deviceId: payload.deviceId,
                      cartId: payload.deviceId,
                      lat: payload.location.lat,
                      lon: payload.location.lon,
                      lng: payload.location.lon,
                      createdOn: payload.location.createdOn,
                  },
              ] as CartMap[]),
    );

export const closeSession = (session: { endedOn: string; images: unknown; sessionGuid: string }) => {
    setDevices((devices) =>
        updateSession(
            devices,
            (d) => d.sessionGuid === session.sessionGuid,
            (d) => ({ ...d, sessionGuid: undefined }),
        ),
    );
    endSession(session);
};

export const updateTicket = (ticket: CustomerTicket) => {
    const payload = transformDevice(ticket) as StoreCustomerTicket;
    setDevices((devices) => updateTicketOfDevices(devices, payload.id ?? '', payload, true));
};

const removeTicket = (ticketId: string) => setDevices((devices) => updateTicketOfDevices(devices, ticketId));

export const updateWeights = (payload: { sessionGuid: string; actualWeight?: number; expectedWeight?: number; t: any }) => {
    setDevices((devices) =>
        devices.map((device: SessionActiveDevice & { weightUpdatedOn?: Date }) => {
            if (device.sessionGuid !== payload.sessionGuid) return device;
            if (new Date(device.weightUpdatedOn ?? 0).getTime() > new Date(payload.t).getTime()) return device;

            return {
                ...device,
                actualWeight: payload.actualWeight ?? device.actualWeight,
                expectedWeight: payload.expectedWeight ?? device.expectedWeight,
                weightUpdatedOn: payload.t,
            };
        }),
    );
    updateSessionWeights(payload);
};

export const updateDeviceLocations = (msg: any) => {
    const payload = msg.map(transformDevice) as SessionActiveDevice[];

    setDevices((devices) =>
        payload.reduce(
            (devices, { deviceId, location }) => updateSession(devices, deviceId ?? '', (d) => ({ ...d, deviceId, location }), true),
            devices,
        ),
    );
    updateSessionLocations(payload);
};

export const updateCustomerLabels = (customerGuid: string, customerLabels: CustomerLabel[]) => {
    const labels = customerLabels.filter((label) => !label.deletedOn).map((label) => label.label);

    setDevices((devices) =>
        devices.map((device) => {
            if (device.customer?.userGuid !== customerGuid) {
                return device;
            }

            return { ...device, customer: { ...device.customer, labels } };
        }),
    );
    updateSessionCustomerLabels(customerGuid, customerLabels);
};

export const closeTickets = async (ticketId: string) => {
    const storeGuid = await awaitStoreGuid();
    const response = await endpoint('storeManager.closeTickets', { storeGuid }).post({ ticketId });
    if (!response) {
        throw Error('Failed to close ticket');
    }
    removeTicket(ticketId);
};

export async function assignTicket(
    storeGuid: string,
    { userName, deviceId, ticketIds }: { userName?: string; deviceId: string; ticketIds: string[] },
) {
    return await endpoint('storeManager.assignTickets', { storeGuid }).post({ userName, deviceId, ticketIds });
}

export async function unassignTicket(storeGuid: string, { ticketIds }: { ticketIds: string[] }) {
    return await endpoint('storeManager.unassignTickets', { storeGuid }).post({ ticketIds });
}
